import { AgreeToTermsPolicy, SignInIdentifier } from '@logto/schemas';
import classNames from 'classnames';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import WebAuthnContext from '@/Providers/WebAuthnContextProvider/WebAuthnContext';
import { InputField, PasswordInputField } from '@/components/InputFields';
import CaptchaBox from '@/containers/CaptchaBox';
import ForgotPasswordLink from '@/containers/ForgotPasswordLink';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import usePasswordSignIn from '@/hooks/use-password-sign-in';
import usePrefilledIdentifier from '@/hooks/use-prefilled-identifier';
import useSendVerificationCode from '@/hooks/use-send-verification-code';
import { useForgotPasswordSettings } from '@/hooks/use-sie';
import useSingleSignOnWatch from '@/hooks/use-single-sign-on-watch';
import useTerms from '@/hooks/use-terms';
import Button from '@/shared/components/Button';
import { UserFlow } from '@/types';
import { getGeneralIdentifierErrorMessage } from '@/utils/form';
import { resolveLoginIdentifier } from '@/utils/login-identifier';

import styles from './index.module.scss';

type Props = {
  readonly className?: string;
  readonly autoFocus?: boolean;
  readonly signInMethods: SignInIdentifier[];
  readonly verificationCodeMethods?: SignInIdentifier[];
};
export type FormState = { identifier: string; password: string };
const passwordMethods = [SignInIdentifier.Username, SignInIdentifier.Phone, SignInIdentifier.Email];
const contacts = [SignInIdentifier.Phone, SignInIdentifier.Email] as const;

const PasswordSignInForm = ({
  className,
  autoFocus,
  signInMethods,
  verificationCodeMethods = [],
}: Props) => {
  const { t, i18n } = useTranslation();
  const zh = i18n.resolvedLanguage?.toLowerCase().startsWith('zh') ?? false;
  const passwordSignIn = usePasswordSignIn();
  const codeSignIn = useSendVerificationCode(UserFlow.SignIn);
  const { isForgotPasswordEnabled } = useForgotPasswordSettings();
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const { setIdentifierInputValue, clearVerificationContext } = useContext(UserInteractionContext);
  const { isPasskeyFlowProcessing } = useContext(WebAuthnContext);
  const prefilled = usePrefilledIdentifier({ enabledIdentifiers: signInMethods });
  const [mode, setMode] = useState<'password' | 'code'>(signInMethods.length ? 'password' : 'code');
  const [contact, setContact] = useState<SignInIdentifier.Phone | SignInIdentifier.Email>(
    verificationCodeMethods.includes(SignInIdentifier.Phone)
      ? SignInIdentifier.Phone
      : SignInIdentifier.Email
  );
  const [selection, setSelection] = useState<SignInIdentifier>(
    prefilled.type ??
      passwordMethods.find((method) => signInMethods.includes(method)) ??
      SignInIdentifier.Username
  );
  const [country, setCountry] = useState('');
  const submitting = useRef(false);
  const {
    register,
    watch,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormState>({
    reValidateMode: 'onBlur',
    shouldUnregister: true,
    defaultValues: {
      identifier:
        prefilled.type === SignInIdentifier.Phone && prefilled.value
          ? '+' + prefilled.value.replace(/^\+/, '')
          : prefilled.type === SignInIdentifier.Username && prefilled.value
            ? '@' + prefilled.value
            : prefilled.value,
      password: '',
    },
  });
  const value = watch('identifier');
  const activeMethod = mode === 'password' ? selection : contact;
  const methods = mode === 'password' ? passwordMethods : contacts;
  const methodLabel = (method: SignInIdentifier) =>
    t(
      method === SignInIdentifier.Username
        ? 'input.username'
        : method === SignInIdentifier.Phone
          ? 'input.phone_number'
          : 'input.email'
    );
  const resolved = resolveLoginIdentifier(value, activeMethod, country);
  const busy = isSubmitting || isPasskeyFlowProcessing;
  const enabled =
    mode === 'password'
      ? signInMethods.includes(selection)
      : verificationCodeMethods.includes(contact);
  const errorMessage = mode === 'password' ? passwordSignIn.errorMessage : codeSignIn.errorMessage;
  const ssoInput = useMemo(() => {
    const result = resolveLoginIdentifier(value, selection, country);
    return mode === 'password' && result.ok && result.type === SignInIdentifier.Email
      ? { type: result.type, value: result.provider }
      : undefined;
  }, [value, selection, country, mode]);
  const { showSingleSignOnForm, navigateToSingleSignOn } = useSingleSignOnWatch(ssoInput);

  useEffect(() => {
    clearVerificationContext();
  }, [clearVerificationContext]);
  useEffect(() => {
    if (showSingleSignOnForm) {
      setValue('password', '');
    }
  }, [showSingleSignOnForm, setValue]);

  const clearProof = () => {
    setValue('password', '');
    clearVerificationContext();
    clearErrors();
    passwordSignIn.clearErrorMessage();
    codeSignIn.clearErrorMessage();
  };
  const changeMode = () => {
    if (busy) {
      return;
    }
    clearProof();
    // Retain only a contact explicitly entered by the user, never look one up from a Handle.
    if (resolved.ok && resolved.type !== SignInIdentifier.Username) {
      setContact(resolved.type);
      setValue('identifier', resolved.canonical);
    } else {
      setValue('identifier', '');
    }
    if (mode === 'code') {
      setSelection(contact);
    }
    setMode(mode === 'password' ? 'code' : 'password');
  };
  const changeMethod = (next: SignInIdentifier) => {
    if (busy || next === activeMethod) {
      return;
    }
    clearProof();
    if (mode === 'password') {
      setSelection(next);
    } else if (next !== SignInIdentifier.Username) {
      setContact(next);
    }
    setIdentifierInputValue({ type: next, value: '' });
    setValue('identifier', '');
    setCountry('');
  };
  const identifierField = register('identifier', {
    validate: (input) => {
      if (!input.trim()) {
        return getGeneralIdentifierErrorMessage([activeMethod], 'required');
      }
      const result = resolveLoginIdentifier(input, activeMethod, country);
      if (result.ok) {
        return true;
      }
      if (result.error === 'country_required') {
        return zh ? '请先选择中国区号 +86。' : 'Select the China country code +86 first.';
      }
      if (result.error === 'country_not_enabled') {
        return zh
          ? '目前仅支持中国大陆 +86 手机号。'
          : 'Only mainland China +86 phone numbers are supported.';
      }
      return getGeneralIdentifierErrorMessage([activeMethod], 'invalid');
    },
  });
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy || submitting.current || !enabled) {
      return;
    }
    submitting.current = true;
    passwordSignIn.clearErrorMessage();
    codeSignIn.clearErrorMessage();
    try {
      await handleSubmit(async ({ identifier: input, password }) => {
        const result = resolveLoginIdentifier(input, activeMethod, country);
        if (!result.ok) {
          return;
        }
        if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
          return;
        }
        const { type } = result;
        const nativeValue = type === SignInIdentifier.Username ? result.canonical : result.provider;
        setIdentifierInputValue({ type, value: nativeValue });
        if (showSingleSignOnForm) {
          await navigateToSingleSignOn();
          return;
        }
        if (mode === 'password') {
          await passwordSignIn.onSubmit({ identifier: { type, value: nativeValue }, password });
        } else if (type !== SignInIdentifier.Username) {
          await codeSignIn.onSubmit({ identifier: type, value: result.provider });
        }
      })(event);
    } finally {
      submitting.current = false;
    }
  };
  const showCountry = activeMethod === SignInIdentifier.Phone;
  const label = methodLabel(activeMethod);

  return (
    <form className={classNames(styles.form, className)} onSubmit={submit} noValidate>
      <div
        className={styles.methodTabs}
        role="tablist"
        aria-label={
          mode === 'password'
            ? zh
              ? '密码登录方式'
              : 'Password sign-in method'
            : zh
              ? '验证码登录方式'
              : 'Verification code method'
        }
      >
        {methods.map((method, index) => (
          <button
            key={method}
            type="button"
            role="tab"
            id={'login-' + method}
            aria-selected={activeMethod === method}
            aria-controls="login-identifier-panel"
            tabIndex={activeMethod === method ? 0 : -1}
            disabled={busy}
            onClick={() => changeMethod(method)}
            onKeyDown={(event) => {
              const next =
                event.key === 'Home'
                  ? 0
                  : event.key === 'End'
                    ? methods.length - 1
                    : event.key === 'ArrowRight'
                      ? (index + 1) % methods.length
                      : event.key === 'ArrowLeft'
                        ? (index - 1 + methods.length) % methods.length
                        : undefined;
              if (next === undefined) {
                return;
              }
              event.preventDefault();
              changeMethod(methods[next]!);
              document.getElementById('login-' + methods[next])?.focus();
            }}
          >
            {methodLabel(method)}
          </button>
        ))}
      </div>
      <div id="login-identifier-panel" role="tabpanel" aria-labelledby={'login-' + activeMethod}>
        {showCountry && (
          <label className={styles.country}>
            {zh ? '国家 / 地区' : 'Country / region'}
            <select
              aria-label={zh ? '国家 / 地区' : 'Country / region'}
              value={country}
              disabled={busy || !enabled}
              onChange={(event) => {
                clearProof();
                setCountry(event.target.value);
              }}
            >
              <option value="">{zh ? '请选择区号' : 'Choose a country code'}</option>
              <option value="+86">{zh ? '中国大陆 +86' : 'Mainland China +86'}</option>
            </select>
          </label>
        )}
        <InputField
          autoFocus={autoFocus}
          className={styles.inputField}
          label={label}
          aria-label={label}
          aria-invalid={Boolean(errors.identifier)}
          aria-describedby={
            errors.identifier
              ? 'login-input-error'
              : mode === 'code'
                ? 'login-input-hint'
                : undefined
          }
          autoComplete={
            mode === 'password' ? 'username' : contact === SignInIdentifier.Phone ? 'tel' : 'email'
          }
          inputMode={
            activeMethod === SignInIdentifier.Phone
              ? 'tel'
              : activeMethod === SignInIdentifier.Email
                ? 'email'
                : 'text'
          }
          disabled={busy || !enabled}
          {...identifierField}
          onChange={(event) => {
            clearProof();
            void identifierField.onChange(event);
          }}
          isDanger={Boolean(errors.identifier)}
        />
        {mode === 'code' && (
          <p className={styles.message} id="login-input-hint">
            {zh
              ? '验证码仅用于登录已有账号，不会自动注册。'
              : 'This code signs in to an existing account. It will not create one.'}
          </p>
        )}
      </div>
      {showSingleSignOnForm && (
        <p role="status" className={styles.message}>
          {t('description.single_sign_on_enabled')}
        </p>
      )}
      {mode === 'password' && !showSingleSignOnForm && (
        <PasswordInputField
          className={styles.inputField}
          autoComplete="current-password"
          label={t('input.password')}
          aria-label={t('input.password')}
          disabled={busy || !enabled}
          isDanger={Boolean(errors.password)}
          errorMessage={errors.password?.message}
          {...register('password', {
            required: mode === 'password' ? t('error.password_required') : false,
          })}
        />
      )}
      {(errors.identifier || errorMessage) && (
        <div id="login-input-error" role="alert" className={styles.formErrors}>
          {errors.identifier?.message || errorMessage}
        </div>
      )}
      {!enabled && (
        <p role="status" className={styles.message}>
          {zh
            ? '当前方式暂不可用，请选择其他登录方式。'
            : 'This method is currently unavailable. Choose another sign-in method.'}
        </p>
      )}
      {isForgotPasswordEnabled && mode === 'password' && !showSingleSignOnForm && (
        <ForgotPasswordLink
          className={styles.link}
          identifier={
            resolved.ok && resolved.type !== SignInIdentifier.Username ? resolved.type : undefined
          }
          value={
            resolved.ok && resolved.type !== SignInIdentifier.Username ? resolved.provider : ''
          }
        />
      )}
      <TermsAndPrivacyCheckbox
        className={classNames(
          styles.terms,
          agreeToTermsPolicy !== AgreeToTermsPolicy.Manual && styles.hidden
        )}
      />
      <CaptchaBox />
      <Button
        name="submit"
        title={
          showSingleSignOnForm
            ? 'action.single_sign_on'
            : mode === 'password'
              ? 'action.sign_in'
              : 'action.continue'
        }
        htmlType="submit"
        isLoading={busy}
        isDisabled={!enabled}
      />
      <div className={styles.alternative}>
        <span>{zh ? '或' : 'or'}</span>
        <button type="button" disabled={busy} onClick={changeMode}>
          {mode === 'password'
            ? zh
              ? '使用验证码登录'
              : 'Sign in with a verification code'
            : zh
              ? '使用密码登录'
              : 'Sign in with a password'}
        </button>
      </div>
    </form>
  );
};

export default PasswordSignInForm;
